import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchApiKeys, generateApiKey, revokeApiKey } from '../../redux/settingsSlice';
import ToastService from '../../core/notifications/ToastService';

export const ApiKeysPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { apiKeys, isLoading, error } = useSelector((state: RootState) => state.settings);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [openKeyDialog, setOpenKeyDialog] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');

  useEffect(() => {
    dispatch(fetchApiKeys());
  }, [dispatch]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await dispatch(generateApiKey(keyName)).unwrap();
      setGeneratedKey(result.data.api_key);
      setOpenDialog(false);
      setOpenKeyDialog(true);
      setKeyName('');
      await dispatch(fetchApiKeys());
      ToastService.success('API key generated successfully');
    } catch (err: any) {
      ToastService.error(err || 'Failed to generate API key');
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await dispatch(revokeApiKey(keyId)).unwrap();
      await dispatch(fetchApiKeys());
      ToastService.success('API key revoked successfully');
    } catch (err: any) {
      ToastService.error(err || 'Failed to revoke API key');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(generatedKey);
    ToastService.success('API key copied to clipboard');
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <IconButton
            onClick={() => navigate('/settings')}
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/settings')}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2rem' } }}>
              API Keys
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
              Manage API keys for integrations
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ backgroundColor: '#14B8A6', width: { xs: '100%', sm: 'auto' } }}
        >
          Generate API Key
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading && apiKeys.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : apiKeys.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No API keys generated yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              Generate an API key to integrate with external systems
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{ backgroundColor: '#14B8A6' }}
            >
              Generate Your First API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop View */}
          <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Key Prefix</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Used</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usage Count</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {key.prefix}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>{key.usage_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={key.is_active ? 'Active' : 'Revoked'}
                        color={key.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {key.is_active && (
                        <IconButton
                          size="small"
                          onClick={() => handleRevoke(key.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Grid container spacing={2}>
              {apiKeys.map((key) => (
                <Grid item xs={12} key={key.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {key.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontFamily: 'monospace' }}>
                            {key.prefix}...
                          </Typography>
                        </Box>
                        <Chip
                          label={key.is_active ? 'Active' : 'Revoked'}
                          color={key.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Last used: {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                        Usage: {key.usage_count} times
                      </Typography>
                      {key.is_active && (
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleRevoke(key.id)}
                          color="error"
                        >
                          Revoke
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      {/* Generate API Key Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate API Key</DialogTitle>
        <form onSubmit={handleGenerate}>
          <DialogContent>
            <TextField
              fullWidth
              label="API Key Name"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="e.g., Mobile App Integration"
              required
              helperText="Give your API key a descriptive name"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#14B8A6' }}>
              Generate
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Show Generated Key Dialog */}
      <Dialog open={openKeyDialog} onClose={() => setOpenKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save this API key securely. You won't be able to see it again!
          </Alert>
          <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {generatedKey}
            </Typography>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleCopyKey}
          >
            Copy to Clipboard
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenKeyDialog(false)} variant="contained" sx={{ backgroundColor: '#14B8A6' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiKeysPage;
